import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import BlogService from '../../../services/BlogService';
import { BlogPost } from '../../../types';
import { LatestAnnouncementSection } from '../../../types';

/**
 * Magazine/Announcement Section
 * Displays magazine promotion and latest announcements
 * Matches original UI with animations and complex layout
 */

interface MagazineSectionProps {
  data: LatestAnnouncementSection;
  fallbackPosts?: BlogPost[];
}

const MagazineSection: React.FC<MagazineSectionProps> = ({ data, fallbackPosts = [] }) => {
  const [posts, setPosts] = useState<BlogPost[]>(fallbackPosts);
  const [loading, setLoading] = useState(true);

  // Refs and animation setup
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await BlogService.getIELTSBlogPosts(2);
        if (response.statusCode === 200 && response.data?.docs && response.data.docs.length > 0) {
          setPosts(response.data.docs);
        } else {
          setPosts(fallbackPosts.slice(0, 2));
        }
      } catch (error) {
        console.warn('Error loading posts, using fallback data:', error);
        setPosts(fallbackPosts.slice(0, 2));
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [fallbackPosts]);

  // Animation variants
  const containerVariants = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 80,
        damping: 15,
      },
    },
  };

  // Helper function to get emoji based on blog type
  const getPriorityEmoji = (blogType?: string) => {
    switch (blogType?.toLowerCase()) {
      case 'tips':
        return '🔥';
      case 'announcement':
        return '📢';
      case 'ielts':
        return '📝';
      default:
        return '📌';
    }
  };

  // Helper function to get background color based on blog type
  const getPriorityBgColor = (blogType?: string) => {
    switch (blogType?.toLowerCase()) {
      case 'tips':
        return 'bg-red-100 dark:bg-red-900/30';
      case 'announcement':
        return 'bg-[#e6f7f5] dark:bg-[#017e6e]/20';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30';
    }
  };

  return (
    <section
      ref={sectionRef}
      className="py-12 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
      id="magazine"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <div className="relative py-6">
            {/* Background decorative elements */}
            <div className="absolute -top-8 -left-8 w-16 h-16 rounded-full bg-gradient-to-br from-teal-300/30 to-teal-500/30 blur-xl dark:from-teal-700/30 dark:to-teal-900/30"></div>
            <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full bg-gradient-to-tr from-emerald-400/40 to-cyan-400/40 blur-lg dark:from-emerald-700/40 dark:to-cyan-700/40"></div>

            {/* Decorative icon */}
            <motion.div
              initial={{ scale: 0, rotate: -45, opacity: 0 }}
              animate={isInView ? { scale: 1, rotate: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
              className="mx-auto mb-4 w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-400 dark:from-teal-400 dark:to-emerald-300 rounded-lg flex items-center justify-center shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-center text-2xl font-bold sm:text-3xl lg:text-4xl text-gray-800 dark:text-gray-100"
            >
              Thông báo{' '}
              <motion.span
                initial={{
                  backgroundPosition: '0% 100%',
                  opacity: 0,
                }}
                animate={
                  isInView
                    ? {
                        backgroundPosition: '100% 100%',
                        opacity: 1,
                      }
                    : {}
                }
                transition={{
                  duration: 1.2,
                  delay: 0.5,
                  ease: 'easeOut',
                }}
                className="relative"
              >
                <span className="relative z-10 bg-gradient-to-r from-[#017e6e] via-[#01a798] to-emerald-400 dark:from-emerald-300 dark:via-teal-400 dark:to-cyan-300 bg-clip-text text-transparent">
                  mới nhất
                </span>
              </motion.span>
            </motion.h2>

            {/* Animated underline */}
            <div className="relative mx-auto mt-4" style={{ maxWidth: '280px' }}>
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={isInView ? { width: '100%', opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="h-1 sm:h-1.5 bg-gradient-to-r from-teal-500 via-emerald-400 to-cyan-400 dark:from-teal-600 dark:via-emerald-500 dark:to-cyan-500 rounded-full"
              ></motion.div>

              {/* Animated highlight */}
              <motion.div
                initial={{ scale: 0, x: '100%' }}
                animate={
                  isInView
                    ? {
                        scale: [0, 1.5, 1],
                        x: ['100%', '0%', '0%', '-100%'],
                      }
                    : {}
                }
                transition={{
                  duration: 2.2,
                  delay: 0.9,
                  repeat: Infinity,
                  repeatDelay: 3,
                }}
                className="absolute top-0 h-1 sm:h-1.5 w-8 bg-white/80 dark:bg-white/40 rounded-full blur-sm"
              ></motion.div>
            </div>

            {/* Decorative dots */}
            <div className="flex justify-center space-x-2 mt-4">
              {[0, 1, 2].map((dot) => (
                <motion.div
                  key={dot}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{
                    duration: 0.4,
                    delay: 1 + dot * 0.1,
                    type: 'spring',
                    stiffness: 400,
                    damping: 10,
                  }}
                  className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 dark:from-teal-400 dark:to-emerald-300"
                ></motion.div>
              ))}
            </div>
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg px-4 sm:px-0 relative z-10 mt-4">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="block"
            >
              {data?.description ||
                'Hệ thống các khóa học IELTS chất lượng cao, giúp bạn chinh phục mục tiêu IELTS một cách nhanh chóng'}
            </motion.span>
          </p>
        </motion.div>

        {/* Main Content Area */}
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={containerVariants}
          className="max-w-7xl mx-auto"
        >
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main magazine section (2/3) - Hidden on small screens */}
            <motion.div variants={itemVariants} className="hidden lg:block lg:w-2/3">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden h-full border border-gray-100 dark:border-gray-700">
                <div className="flex flex-col md:flex-row p-6">
                  {/* Left text content */}
                  <div className="md:w-7/12 md:pr-6 flex flex-col justify-center">
                    <h2 className="text-3xl font-bold text-[#017e6e] mb-2">
                      Tạp chí <span className="text-red-600">giải đề IELTS</span>
                    </h2>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">chi tiết từng bước</h3>

                    <div className="inline-block bg-[#017e6e]/10 rounded-full px-3 py-1 text-sm text-[#017e6e] dark:text-[#5de0cd] mb-4 w-fit">
                      VOL.1 | THÁNG 04, 2026
                    </div>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#017e6e]/10 mr-2">
                          <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                        </div>
                        <span className="text-gray-800 dark:text-gray-200 text-sm">Giải đề thi IELTS chi tiết</span>
                      </li>
                      <li className="flex items-center">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#017e6e]/10 mr-2">
                          <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                        </div>
                        <span className="text-gray-800 dark:text-gray-200 text-sm">Mẹo trả lời hiệu quả</span>
                      </li>
                      <li className="flex items-center">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#017e6e]/10 mr-2">
                          <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                        </div>
                        <span className="text-gray-800 dark:text-gray-200 text-sm">Phân tích chi tiết các câu hỏi</span>
                      </li>
                    </ul>

                    <div className="relative inline-block group">
                      <a
                        href="#"
                        className="inline-block bg-[#017e6e] hover:bg-[#016d5f] text-white font-bold rounded-full px-6 py-3 transition duration-300 ease-in-out shadow-md hover:shadow-lg text-sm"
                      >
                        ĐĂNG KÝ NHẬN TẠP CHÍ TẠI ĐÂY
                      </a>
                      <motion.span
                        className="absolute -right-8 top-2 text-xl"
                        animate={{ x: [0, 5, 0] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        ➡️
                      </motion.span>
                    </div>
                  </div>

                  {/* Right magazine cover */}
                  <div className="md:w-5/12 mt-8 md:mt-0 relative">
                    {/* Decorative rotating element */}
                    <motion.div
                      className="absolute -left-8 top-1/4 w-12 h-12 z-0"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    >
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#017e6e" d="M50 0 L55 40 L95 50 L55 60 L50 100 L45 60 L5 50 L45 40 Z"></path>
                      </svg>
                    </motion.div>

                    {/* Magazine cover */}
                    <motion.div
                      className="relative z-10 bg-white rounded-lg shadow-xl overflow-hidden border-6 border-white"
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 3 }}
                      transition={{
                        duration: 0.8,
                        type: 'spring',
                        stiffness: 100,
                        damping: 10,
                      }}
                      style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
                    >
                      <div className="bg-gradient-to-br from-[#e6f7f5] to-[#f9e3e3] p-3">
                        <div className="text-center mb-2">
                          <span className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                            IELTS INSIGHTS
                          </span>
                        </div>
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-xs text-gray-600">EDUCATIONAL</div>
                          <div className="text-xs text-gray-600">VOL.1</div>
                        </div>

                        <div className="aspect-[3/4] bg-white rounded-md shadow-md flex items-center justify-center mb-3 overflow-hidden">
                          <div className="w-full h-full bg-gradient-to-br from-[#017e6e] to-emerald-500 flex items-center justify-center">
                            <div className="text-center text-white">
                              <div className="text-4xl font-bold">IELTS</div>
                              <div className="text-sm">INSIGHTS</div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between text-xs text-gray-600">
                          <div>JENNIFER'S</div>
                          <div>ACADEMY</div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Reflection effect */}
                    <div
                      className="absolute inset-x-5 -bottom-6 h-10 bg-gradient-to-b from-[#017e6e]/20 to-transparent rounded-b-full blur-md opacity-60 dark:opacity-40"
                      style={{ transform: 'rotateX(60deg) scale(0.85)', transformOrigin: 'center bottom' }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Sidebar - Latest announcements (1/3) */}
            <motion.div variants={itemVariants} className="lg:w-1/3 w-full">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700">
                <div className="p-4 bg-gradient-to-r from-[#017e6e] to-[#016d5f] text-white">
                  <h3 className="font-bold text-lg flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    Trung tâm thông báo
                  </h3>
                </div>

                <div className="p-4">
                  <div className="space-y-4">
                    {loading ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="w-8 h-8 border-4 border-[#017e6e]/30 border-t-[#017e6e] rounded-full animate-spin"></div>
                      </div>
                    ) : posts.length === 0 ? (
                      <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-center">
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Không có thông báo nào</p>
                      </div>
                    ) : (
                      posts.map((post) => (
                        <motion.div
                          key={post._id}
                          className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-300"
                          whileHover={{ scale: 1.02, y: -3 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 10 }}
                        >
                          <div className="flex items-center mb-2">
                            <div
                              className={`w-9 h-9 rounded-full ${getPriorityBgColor(
                                post.blogType
                              )} flex items-center justify-center mr-3 flex-shrink-0`}
                            >
                              <span className="text-[#017e6e] dark:text-[#5de0cd] text-lg">
                                {getPriorityEmoji(post.blogType)}
                              </span>
                            </div>
                            <h4 className="font-semibold text-gray-800 dark:text-white text-sm line-clamp-2">
                              {post.title}
                            </h4>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 line-clamp-2">{post.excerpt}</p>
                          <a
                            href={`#`}
                            className="text-[#017e6e] hover:text-[#016d5f] dark:text-[#5de0cd] text-sm font-medium hover:underline inline-flex items-center"
                          >
                            Xem chi tiết
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 ml-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </a>
                        </motion.div>
                      ))
                    )}
                  </div>

                  <div className="mt-5 text-center">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <a
                        href="#"
                        className="inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300"
                      >
                        Xem tất cả thông báo
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 ml-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </a>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Mobile-only magazine section */}
              <div className="lg:hidden mt-6">
                <motion.div
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700"
                >
                  <div className="p-6">
                    <div className="flex flex-col items-center">
                      <motion.div
                        className="relative w-48 mb-6"
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 3 }}
                        transition={{
                          duration: 0.8,
                          type: 'spring',
                          stiffness: 100,
                          damping: 10,
                        }}
                        style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
                      >
                        <div className="bg-gradient-to-br from-[#e6f7f5] to-[#f9e3e3] p-3 rounded-lg shadow-xl">
                          <div className="text-center mb-2">
                            <span className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                              IELTS INSIGHTS
                            </span>
                          </div>
                          <div className="aspect-[3/4] bg-white rounded-md shadow-md flex items-center justify-center mb-3 overflow-hidden">
                            <div className="w-full h-full bg-gradient-to-br from-[#017e6e] to-emerald-500 flex items-center justify-center">
                              <div className="text-center text-white">
                                <div className="text-4xl font-bold">IELTS</div>
                                <div className="text-sm">INSIGHTS</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      <div className="text-center">
                        <h2 className="text-xl font-bold text-[#017e6e] mb-2">
                          Tạp chí <span className="text-red-600">giải đề IELTS</span>
                        </h2>
                        <div className="inline-block bg-[#017e6e]/10 rounded-full px-3 py-1 text-sm text-[#017e6e] dark:text-[#5de0cd] mb-4">
                          VOL.1 | THÁNG 04, 2026
                        </div>

                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="mt-2">
                          <a
                            href="#"
                            className="inline-block bg-[#017e6e] hover:bg-[#016d5f] text-white font-bold rounded-full px-6 py-3 transition duration-300 ease-in-out shadow-md hover:shadow-lg text-sm"
                          >
                            ĐĂNG KÝ NHẬN TẠP CHÍ
                          </a>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MagazineSection;
